import { useState } from 'react';
import { useAttachStatement } from '../../hooks/useReconciliation';
import { ApiError } from '../../lib/api';

function apiErrorMessage(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | null)?.error ?? 'Request failed';
  }
  return err instanceof Error ? err.message : undefined;
}

/**
 * Drop the bank statement on the reconciliation: one action stores it in the
 * vault, files it under the month it covers, and links it as this
 * reconciliation's evidence.
 *
 * Offered on an in-progress session AND on a completed one. A completed
 * reconciliation with no statement behind it is exactly the case that needs
 * repairing — the proof sheet is filed, the packet is thin, and there was no
 * way to put the PDF in without voiding the work.
 */
export function StatementDropZone({
  companyId,
  reconId,
  busyLabel,
  idleLabel,
  onAttached,
}: {
  companyId: string;
  reconId: string;
  /** Shown while a follow-on action (e.g. the AI read) is still running. */
  busyLabel?: string | null;
  idleLabel: string;
  onAttached?: (r: { statementId: string; fileName: string }) => void;
}) {
  const attach = useAttachStatement(companyId);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File) => {
    setError(null);
    attach.mutate(
      { id: reconId, file },
      {
        onSuccess: (r) => onAttached?.(r),
        onError: (e) => setError(apiErrorMessage(e) ?? 'Could not attach the statement'),
      },
    );
  };

  return (
    <>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) handleFile(f);
        }}
        style={{
          flex: 1,
          minWidth: 200,
          padding: '8px 12px',
          fontSize: 12,
          textAlign: 'center',
          cursor: 'pointer',
          borderRadius: 6,
          border: `1px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
          background: dragOver ? 'var(--bg-surface)' : undefined,
        }}
      >
        <input
          type="file"
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = '';
          }}
        />
        {attach.isPending ? 'Uploading statement…' : (busyLabel ?? idleLabel)}
      </label>
      {error && (
        <div className="field-error" style={{ fontSize: 12, width: '100%' }}>
          {error}
        </div>
      )}
    </>
  );
}
